import React, { forwardRef, useImperativeHandle, useRef, useState, useCallback } from 'react';
import { Paper, List, ListItem, ListItemAvatar, Avatar, ListItemText, ClickAwayListener } from '@mui/material';

export const MENTION_CLASS = 'mention-chip';

/**
 * Serializes the live contentEditable DOM into the storage string format
 * "Hello @[Name](id), how are you". No parsing of typed text is needed —
 * mentions only ever exist as atomic <span data-mention-id> nodes inserted
 * by insertMention, so this just walks the DOM once at send time.
 */
function serializeNode(node) {
    let result = '';
    node.childNodes.forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
            result += child.textContent;
        } else if (child.nodeType === Node.ELEMENT_NODE) {
            if (child.dataset && child.dataset.mentionId) {
                result += `@[${child.dataset.mentionName}](${child.dataset.mentionId})`;
            } else if (child.tagName === 'BR') {
                result += '\n';
            } else if (child.tagName === 'DIV' || child.tagName === 'P') {
                // Some browsers wrap each new line in its own <div> inside contentEditable
                if (result && !result.endsWith('\n')) result += '\n';
                result += serializeNode(child);
            } else {
                result += serializeNode(child);
            }
        }
    });
    return result;
}

function getCaretRect(editorEl) {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return null;
    const range = sel.getRangeAt(0).cloneRange();
    range.collapse(true);
    let rect = range.getClientRects()[0];
    if (!rect) {
        // Empty line / start of editor has no client rect — probe with a marker
        const marker = document.createElement('span');
        marker.textContent = '\u200b';
        range.insertNode(marker);
        rect = marker.getBoundingClientRect();
        marker.parentNode.removeChild(marker);
        editorEl.normalize();
    }
    return rect;
}

// Looks backward from the caret, within the current text node only, for an
// unfinished "@query" run (stops at whitespace or if there's no "@").
function findMentionQuery() {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return null;
    const node = sel.anchorNode;
    if (!node || node.nodeType !== Node.TEXT_NODE) return null;
    const offset = sel.anchorOffset;
    const textBefore = node.textContent.slice(0, offset);
    const atIndex = textBefore.lastIndexOf('@');
    if (atIndex === -1) return null;
    const query = textBefore.slice(atIndex + 1);
    if (/\s/.test(query)) return null;
    return { node, start: atIndex, end: offset, query };
}

const initials = (name) =>
    (name || '?').split(' ').filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('');

/**
 * Uncontrolled contentEditable mention input.
 *
 * Deliberately uncontrolled: the DOM is the single source of truth while
 * typing, so React never re-renders the editable surface mid-keystroke and
 * the caret can never desync from what's on screen (the whole class of bug
 * react-mentions' textarea+overlay approach is prone to).
 *
 * Parent reads the final value via ref.getValue() (e.g. on Send) and clears
 * via ref.clear(). onDirtyChange(bool) fires on every input so the parent
 * can enable/disable a submit button without touching the DOM itself.
 */
const MentionEditor = forwardRef(function MentionEditor(
    { users = [], placeholder = '', onDirtyChange, className, style },
    ref
) {
    const editorRef = useRef(null);
    const matchRef = useRef(null);

    const [suggestOpen, setSuggestOpen] = useState(false);
    const [suggestQuery, setSuggestQuery] = useState('');
    const [suggestPos, setSuggestPos] = useState({ top: 0, left: 0 });
    const [activeIndex, setActiveIndex] = useState(0);

    const filteredUsers = suggestOpen
        ? users.filter((u) => (u.display || '').toLowerCase().includes(suggestQuery.toLowerCase())).slice(0, 8)
        : [];

    useImperativeHandle(ref, () => ({
        getValue: () => (editorRef.current ? serializeNode(editorRef.current).trim() : ''),
        clear: () => {
            if (editorRef.current) editorRef.current.innerHTML = '';
            onDirtyChange(false);
            setSuggestOpen(false);
        },
        focus: () => editorRef.currentfocus(),
    }));

    const closeSuggestions = useCallback(() => {
        setSuggestOpen(false);
        matchRef.current = null;
    }, []);

    const insertMention = useCallback(
        (user) => {
            const match = matchRef.current;
            if (!match || !editorRef.current) return;

            const { node, start, end } = match;
            const range = document.createRange();
            range.setStart(node, start);
            range.setEnd(node, end);
            range.deleteContents();

            const chip = document.createElement('span');
            chip.contentEditable = 'false';
            chip.className = MENTION_CLASS;
            chip.dataset.mentionId = user.id;
            chip.dataset.mentionName = user.display;
            chip.textContent = `@${user.display}`;

            const trailingSpace = document.createTextNode('\u00A0');

            range.insertNode(trailingSpace);
            range.insertNode(chip);

            const sel = window.getSelection();
            const newRange = document.createRange();
            newRange.setStartAfter(trailingSpace);
            newRange.collapse(true);
            sel.removeAllRanges();
            sel.addRange(newRange);

            editorRef.current.focus();
            closeSuggestions();
            onDirtyChange(editorRef.current.textContent.trim().length > 0);
        },
        [closeSuggestions, onDirtyChange]
    );

    const handleInput = () => {
        const isEmpty = !editorRef.current || editorRef.current.textContent.trim().length === 0;
        onDirtyChange(!isEmpty);

        const match = findMentionQuery();
        if (match && editorRef.current) {
            matchRef.current = match;
            setSuggestQuery(match.query);
            const rect = getCaretRect(editorRef.current);
            const editorRect = editorRef.current.getBoundingClientRect();
            if (rect) {
                setSuggestPos({
                    top: rect.bottom - editorRect.top + 4,
                    left: Math.max(0, rect.left - editorRect.left),
                });
            }
            setActiveIndex(0);
            setSuggestOpen(true);
        } else {
            closeSuggestions();
        }
    };

    const handleKeyDown = (e) => {
        if (!suggestOpen || filteredUsers.length === 0) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((i) => (i + 1) % filteredUsers.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((i) => (i - 1 + filteredUsers.length) % filteredUsers.length);
        } else if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            insertMention(filteredUsers[activeIndex]);
        } else if (e.key === 'Escape') {
            closeSuggestions();
        }
    };

    return (
        <div style={{ position: 'relative' }}>
            <div
                ref={editorRef}
                className={className}
                style={style}
                contentEditable
                suppressContentEditableWarning
                role="textbox"
                aria-multiline="true"
                aria-label={placeholder}
                data-placeholder={placeholder}
                onInput={handleInput}
                onKeyDown={handleKeyDown}
                onBlur={() => setTimeout(closeSuggestions, 150)}
            />
            {suggestOpen && filteredUsers.length > 0 && (
                <ClickAwayListener onClickAway={closeSuggestions}>
                    <Paper
                        elevation={4}
                        style={{
                            position: 'absolute',
                            top: suggestPos.top,
                            left: suggestPos.left,
                            zIndex: 1300,
                            width: 260,
                            maxHeight: 220,
                            overflowY: 'auto',
                        }}
                    >
                        <List dense disablePadding>
                            {filteredUsers.map((u, i) => (
                                <ListItem
                                    key={u.id}
                                    button
                                    selected={i === activeIndex}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => insertMention(u)}
                                >
                                    <ListItemAvatar>
                                        <Avatar style={{ width: 28, height: 28, fontSize: '0.7rem' }}>
                                            {initials(u.display)}
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText primary={u.display} secondary={u.email} />
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                </ClickAwayListener>
            )}
        </div>
    );
});

export default MentionEditor;